import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { BLOGS } from "@/data/content";

export default function Blog() {
  const [feature, ...rest] = BLOGS;
  return (
    <section id="blogs" className="py-24 md:py-32 px-6 lg:px-10" data-testid="blog-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-px bg-[#C9A227]" />
              <span className="overline">From The Journal</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0A2342] max-w-2xl leading-[1.05]">
              Notes from our concierge desk.
            </h2>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Featured */}
          <motion.a
            href="#"
            data-testid="blog-feature"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-3 group relative overflow-hidden rounded-3xl aspect-[4/3] lg:aspect-auto lg:min-h-[520px]"
          >
            <img src={feature.image} alt={feature.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-8 md:p-10 text-white">
              <span className="inline-block px-3 py-1 rounded-full bg-[#C9A227] text-white text-[11px] uppercase tracking-[0.2em] mb-4">{feature.tag}</span>
              <h3 className="font-display text-3xl md:text-4xl leading-[1.1] max-w-2xl">{feature.title}</h3>
              <p className="mt-3 text-white/80 max-w-xl">{feature.excerpt}</p>
              <div className="mt-5 flex items-center gap-2 text-sm text-white/80">
                <span>{feature.read}</span>
                <ArrowUpRight className="w-4 h-4 ml-auto" />
              </div>
            </div>
          </motion.a>

          {/* Side stack */}
          <div className="lg:col-span-2 grid gap-6">
            {rest.slice(0, 3).map((b, i) => (
              <motion.a
                key={b.title}
                href="#"
                data-testid={`blog-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="lux-card flex gap-4 p-3 items-center"
              >
                <div className="w-24 h-24 md:w-28 md:h-28 shrink-0 rounded-2xl overflow-hidden">
                  <img src={b.image} alt={b.title} loading="lazy" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
                </div>
                <div className="py-1 pr-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#C9A227]">{b.tag}</div>
                  <h4 className="font-display text-base md:text-lg text-[#0A2342] leading-snug mt-1">{b.title}</h4>
                  <div className="text-xs text-[#6B7280] mt-1.5">{b.read}</div>
                </div>
              </motion.a>
            ))}
          </div>
        </div>

        {/* Extra row */}
        <div className="mt-6 grid md:grid-cols-2 gap-6">
          {rest.slice(3).map((b, i) => (
            <motion.a
              key={b.title}
              href="#"
              data-testid={`blog-extra-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="lux-card flex gap-4 p-3 items-center"
            >
              <div className="w-28 h-28 shrink-0 rounded-2xl overflow-hidden">
                <img src={b.image} alt={b.title} loading="lazy" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
              </div>
              <div className="py-2 pr-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#C9A227]">{b.tag}</div>
                <h4 className="font-display text-lg text-[#0A2342] leading-snug mt-1">{b.title}</h4>
                <p className="text-xs text-[#6B7280] mt-1.5 line-clamp-2">{b.excerpt}</p>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
